<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('reschedule_new_date') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        v-form(ref="form" v-else-if="appointment")
          div.d-flex.align-center
            div.text-sm.font-semibold.neutral500--text {{ $t('patient_details') }}
            v-spacer
            v-card.round-xl.px-2.py-1(outlined)
              y-checkbox.mb-n2(v-model="overrideAppt" @change="updateOverride(overrideAppt)")
                template(#label)
                  div.d-flex.align-center
                    .text-sm.neutral700--text.mt-1 Override
                    v-tooltip(top color="black")
                      template(#activator="{ on, attrs }")
                        v-icon.ml-2(color="neutral500" size="20" v-bind="attrs" v-on="on" style="margin-top: 2px;") $yzi-info-square
                      div.py-1
                        div.text-xs.font-semibold Override booking
                      div.text-xs Ignore the booking rule and allow last-minute bookings

          v-card.mt-4.pa-4.round-lg(outlined)
            v-row(dense)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('nric_passport') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.ic || '-' }}
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('service') }}
                div.text-sm.neutral700--text.mt-1(v-if="appointment.services.length") {{ appointment.services.map(m => m.name).join(', ') }}
                div.text-sm.neutral700--text.mt-1(v-else) -
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('doctor') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.provider.name || '-' }}
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text Scheduled date and time
                div.d-flex.align-center.mt-1(v-if="appointment.start")
                  div.text-sm.neutral700--text(v-if="appointment.start") {{ appointment.start | humanizeDate }}
                  div.text-sm(v-if="appointment.time_range.length > 1") , {{ appointment.time_range[0] | displayTime2 }} - {{ appointment.time_range[1] | displayTime2 }}  
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('status') }}
                y-chip(:label="getStatus(appointment.status).text" :color="getStatus(appointment.status).color")

          div.text-md.font-semibold.neutral700--text.mt-6 {{ $t('change_appointment_date_time') }}

          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('appointment_date') }}
          y-dialog(v-model='showPicker' width='380px' content-class="round-3xl")
            template(v-slot:activator='{ on }')
              v-text-field.text-sm.mt-2.round-xl(v-model="selectedDate" :placeholder="$t('select_appointment_date')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto" v-on="on" :loading="isFetchingDates")
                template(v-slot:append)
                  eva-icon.mt-1(name="calendar-outline" fill="#848484" v-on="on")
            v-card
              v-card-text.px-2.pt-2.pb-0
                div.d-flex.justify-center.align-center(v-if="isFetchingDates" style="height: 380px;")
                  v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
                yc-date-picker(v-else :parentDate.sync="form.date" color="info" :allowedDates="unavailableDateList" :minDate="getTodayDate()" :parentPickerDate.sync="pickerDate")
              v-card-actions.px-4.pb-4
                v-row(no-gutters)
                  v-col.pr-2
                    v-btn.button-sm(color="neutral700" block dark outlined depressed @click="clearDate") {{ $t('clear') }}
                  v-col.pl-2
                    v-btn.button-sm(color="neutral700" block dark depressed :dark="Boolean(form.date)" :disabled="!form.date" @click="pickDate(appointment.services, appointment.provider.id, form.date)") {{ $t('select') }}

          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('available_time') }}
          y-select.mt-2(v-model="form.time" :items="times" item-text="text" item-value="value" :placeholder="$t('select_available_time')" :rules="rules.not_empty_select" :loading="isFetchingTimes" no-data-text="No timeslot available")

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="confirmAction") {{ $t('confirm') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'RescheduleDate',
  components: {},
  props: ['open', 'selectedAppointment'],
  data () {
    return {
      isFetching: false,
      isFetchingDates: false,
      isFetchingTimes: false,
      isLoading: false,
      showPicker: false,
      overrideAppt: false,
      appointment: null,
      selectedDate: null,
      pickerDate: null,
      form: {},
      unavailableDates: [],
      times: [],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    }
  },
  watch: {
    pickerDate (val) {
      if (this.appointment) {
        this.fetchUnavailableDates(val)
      }
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          this.form = {
            date: this.appointment.date,
            time: this.appointment.time
          }
          this.selectedDate = this.$options.filters.humanizeDate(this.appointment.date)
          this.fetchUnavailableDates()
          this.fetchTimes(this.appointment.services, this.appointment.provider.id, this.appointment.date)
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchUnavailableDates (yearMonth) {
      this.isFetchingDates = true
      let params = {
        services: this.appointment.services.map(m => m.id).join(),
        provider: this.appointment.provider.id
      }
      if (yearMonth) {
        params.year = yearMonth.substr(0, 4),
        params.month = yearMonth.substr(-2)
      }
      if (this.overrideAppt) {
        params.ignore_rules = 'service_prep_time'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/block_dates`, { params })
        .then(resp => {
          this.unavailableDates = resp.data
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetchingDates = false
        })
    },
    fetchTimes (services, provider, date) {
      this.isFetchingTimes = true
      let params = {
        services: services.map(m => m.id).join(),
        date: date,
        reschedule_id: this.selectedAppointment.id
      }
      if (this.overrideAppt) {
        params.ignore_rules = 'service_prep_time'
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/providers/${provider}/available_times`, { params } )
        .then(resp => {
          this.times = resp.data.times.map(m => { return { text: this.$options.filters.displayTime2(m), value: m }})
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetchingTimes = false
        })
    },
    unavailableDateList (val) {
      if (this.unavailableDates.includes(val)) {
        return false
      }
      return true
    },
    pickDate (services, provider, date) {
      this.selectedDate = this.$options.filters.humanizeDate(this.form.date)
      this.showPicker = false
      !this.form.time || this.$set(this.form, 'time', null)
      this.fetchTimes(services, provider, date)
    },
    confirmAction () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const data = {
        service: this.appointment.service.id,
        provider: this.appointment.provider.id,
        start: `${this.form.date} ${this.form.time}`
        // date: this.form.date,
        // time: this.form.time
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const endpoint = `/clinics/${companySlug}/appointments/${this.selectedAppointment.id}`
      this.$axios.patch(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'PATCH', this.selectedAppointment.id)
          this.closeResetDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clearDate () {
      this.$set(this.form, 'date', null)
      this.selectedDate = null
      this.showPicker = false
    },
    clearDateTime () {
      this.times = []
      this.$set(this.form, 'time', null)
      this.clearDate()
      this.fetchUnavailableDates(this.pickerDate)
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    getStatus (status) {
      if (['new','processed'].includes(status)) {
        return { text: 'Upcoming', color: 'green' }
      } else if (status === 'received') {
        return { text: 'Completed', color: 'blue' }
      } else if (status === 'absent') {
        return { text: 'Missed', color: 'red' }
      } else if (status === 'cancelled') {
        return { text: 'Cancelled', color: 'orange' }
      } else {
        return { text: '-', color: 'normal' }
      }
    },
    getTodayDate () {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    updateOverride (override) {
      if (override) {
        this.showMessage(['info', `This override function will ignore the 'booking before X time' rule, allowing last-minute bookings.`])
      }
      this.clearDateTime()
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Reschedule appointment',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>